import { useCallback, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form } from "@patient-watch/ui";
import { formSearchParamsSchema } from "../../types/formSearchParams";
import FormStepWrapper from "../../components/FormStepWrapper";
import FormButtons from "../../components/FormButtons";
import { useFormData } from "../../hooks/useFormData";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import FormLoading from "../../components/FormLoading";
import IntervalFieldCards, { type IntervalErrorInfo } from "../../components/IntervalFieldCards";
import {
    END_HOUR_OPTIONS,
    HOUR_OPTIONS,
    IntervalFormValue,
    mapStoredIntervalsToForm,
    normaliseIntervalsForSave,
} from "../../utils/timeIntervals";
import { useCopy } from "../../i18n/locale";
import type { GlucoseCopy } from "../../i18n/copy";

const createFormSchema = (copy: GlucoseCopy) => {
    const intervalSchema = z
        .object({
            start_time: z.string().min(1, copy.intervals.startRequired),
            end_time: z.string().min(1, copy.intervals.endRequired),
        })
        .superRefine(({ start_time, end_time }, ctx) => {
            if (start_time >= end_time) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: copy.intervals.endAfterStart,
                    path: ["end_time"],
                });
            }
        });

    return z
        .object({
            on_duty_intervals: z.array(intervalSchema).min(1, copy.duty.atLeastOne),
        })
        .superRefine(({ on_duty_intervals }, ctx) => {
            const sorted = [...on_duty_intervals].sort((a, b) => a.start_time.localeCompare(b.start_time));

            let previousEnd: string | null = null;
            sorted.forEach((interval, index) => {
                if (previousEnd && interval.start_time < previousEnd) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: copy.duty.noOverlap,
                        path: ["on_duty_intervals", index, "start_time"],
                    });
                }
                previousEnd = interval.end_time;
            });
        });
};

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

interface SeaWindow {
    start: number;
    end: number;
    label: string;
}

const MINUTES_IN_DAY = 24 * 60;

const toMinutesFromHour = (value: string) => {
    if (value === "24:00") return MINUTES_IN_DAY;
    const [hours, minutes] = value.split(":").map((part) => Number(part));
    return hours * 60 + (minutes ?? 0);
};

const buildSeaWindows = (entryData: Record<string, any> | null): SeaWindow[] => {
    if (!entryData || entryData.at_sea !== true) {
        return [];
    }

    const mapped = mapStoredIntervalsToForm(entryData.at_sea_intervals as IntervalFormValue[] | null | undefined, {
        fallbackFullDay: entryData.at_sea_all_day === true,
    });

    return mapped
        .filter((interval) => interval.start_time && interval.end_time)
        .map((interval) => ({
            start: toMinutesFromHour(interval.start_time),
            end: toMinutesFromHour(interval.end_time),
            label: `${interval.start_time} - ${interval.end_time}`,
        }))
        .filter((window) => window.start !== null && window.end !== null)
        .sort((a, b) => a.start - b.start);
};

const validateDutyWindows = (intervals: IntervalFormValue[], seaWindows: SeaWindow[]) => {
    const invalid: number[] = [];
    intervals.forEach((interval, index) => {
        const start = toMinutesFromHour(interval.start_time);
        const end = toMinutesFromHour(interval.end_time);

        const fitsWindow = seaWindows.some((window) => start >= window.start && end <= window.end);

        if (!fitsWindow) {
            invalid.push(index);
        }
    });

    return invalid;
};

const decorateLabel = (label: string, reason?: string) => (reason ? `${label} (${reason})` : label);

const getDutyStartOptions = (seaWindows: SeaWindow[], notOnWater: string) => {
    if (seaWindows.length === 0) {
        return HOUR_OPTIONS.map((option) => ({
            ...option,
            locked: true,
            label: decorateLabel(option.label, notOnWater),
        }));
    }

    return HOUR_OPTIONS.map((option) => {
        const minutes = toMinutesFromHour(option.value);
        const allowed = seaWindows.some((window) => minutes >= window.start && minutes < window.end);

        return {
            ...option,
            locked: !allowed,
            label: decorateLabel(option.label, allowed ? undefined : notOnWater),
        };
    });
};

const getDutyEndOptions = (seaWindows: SeaWindow[], startValue: string | undefined, messages: GlucoseCopy["duty"]) => {
    if (!startValue) {
        return END_HOUR_OPTIONS.map((option) => ({
            ...option,
            locked: true,
            label: decorateLabel(option.label, messages.chooseStartFirst),
        }));
    }

    if (seaWindows.length === 0) {
        return END_HOUR_OPTIONS.map((option) => ({
            ...option,
            locked: true,
            label: decorateLabel(option.label, messages.notOnWater),
        }));
    }

    const startMinutes = toMinutesFromHour(startValue);
    const window = seaWindows.find((candidate) => startMinutes >= candidate.start && startMinutes < candidate.end);

    if (!window) {
        return END_HOUR_OPTIONS.map((option) => ({
            ...option,
            locked: true,
            label: decorateLabel(option.label, messages.startOutside),
        }));
    }

    return END_HOUR_OPTIONS.map((option) => {
        const endMinutes = toMinutesFromHour(option.value);
        const allowed = endMinutes > startMinutes && endMinutes <= window.end;

        let reason: string | undefined;
        if (!allowed) {
            reason = endMinutes <= startMinutes ? messages.endAfterStart : messages.notOnWater;
        }

        return {
            ...option,
            locked: !allowed,
            label: decorateLabel(option.label, reason),
        };
    });
};

export const Route = createFileRoute("/form/on-duty-intervals")({
    component: WorkScheduleComponent,
    validateSearch: (search) => formSearchParamsSchema.parse(search),
});

function WorkScheduleComponent() {
    const { entry_id, profile_id } = useSearch({
        from: "/form/on-duty-intervals",
    });
    const navigate = useNavigate();
    const copy = useCopy();

    const form = useForm<FormData>({
        resolver: zodResolver(createFormSchema(copy)),
        defaultValues: {
            on_duty_intervals: [{ start_time: "", end_time: "" }],
        },
    });

    const { control, watch, formState, setError } = form;

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "on_duty_intervals",
    });

    const { entryData, isLoading } = useFormData({
        entry_id,
        form,
        fields: [],
    });

    const seaWindows = useMemo(() => buildSeaWindows(entryData ?? null), [entryData]);

    const dutyStartOptions = useMemo(() => getDutyStartOptions(seaWindows, copy.duty.notOnWater), [seaWindows, copy]);

    useEffect(() => {
        if (!entryData || formState.isDirty) {
            return;
        }

        const mapped = mapStoredIntervalsToForm(
            entryData.on_duty_intervals as IntervalFormValue[] | null | undefined
        ).filter((interval) => interval.start_time && interval.end_time);

        replace(mapped.length > 0 ? mapped : [{ start_time: "", end_time: "" }]);
    }, [entryData, replace, formState.isDirty]);

    useEffect(() => {
        if (!entryData || isLoading) return;

        if (entryData.at_sea === false) {
            navigate({
                to: "/form/glucose-issue",
                search: { entry_id, profile_id } as any,
            });
        } else if (
            entryData.at_sea_all_day === false &&
            (!Array.isArray(entryData.at_sea_intervals) || entryData.at_sea_intervals.length === 0)
        ) {
            navigate({
                to: "/form/at-sea-intervals",
                search: { entry_id, profile_id } as any,
            });
        }
    }, [entryData, isLoading, navigate, entry_id, profile_id]);

    const { submitting, submitForm } = useFormSubmission({
        entry_id,
        profile_id,
        nextRoute: "/form/glucose-issue",
    });

    const intervals = watch("on_duty_intervals") ?? [];

    const onSubmit = (data: FormData) => {
        const invalidIndices = validateDutyWindows(data.on_duty_intervals, seaWindows);

        if (invalidIndices.length > 0) {
            invalidIndices.forEach((index) => {
                setError(`on_duty_intervals.${index}.start_time`, {
                    type: "manual",
                    message: copy.duty.withinSeaTime,
                });
                setError(`on_duty_intervals.${index}.end_time`, {
                    type: "manual",
                    message: copy.duty.withinSeaTime,
                });
            });
            return;
        }

        const payload = normaliseIntervalsForSave(data.on_duty_intervals);
        submitForm({
            on_duty_intervals: payload,
        });
    };

    const onPrevious = () => {
        const previousRoute = entryData?.at_sea_all_day === true ? "/form/at-sea-all-day" : "/form/at-sea-intervals";

        navigate({
            to: previousRoute,
            search: { entry_id, profile_id } as any,
        });
    };

    const handleAddInterval = () => {
        append({ start_time: "", end_time: "" });
    };

    const handleRemoveInterval = (index: number) => {
        if (fields.length === 1) {
            replace([{ start_time: "", end_time: "" }]);
            return;
        }
        remove(index);
    };

    const getIntervalErrors = (index: number): IntervalErrorInfo => {
        const errors = formState.errors.on_duty_intervals;
        const fieldError = Array.isArray(errors) ? errors[index] : undefined;

        const startError =
            fieldError && "start_time" in fieldError
                ? (fieldError.start_time as { message?: string })?.message
                : undefined;

        const endError =
            fieldError && "end_time" in fieldError ? (fieldError.end_time as { message?: string })?.message : undefined;

        return { startError, endError };
    };

    const canRemoveInterval = (index: number) => {
        if (fields.length === 1) {
            const interval = intervals?.[index];
            return Boolean(interval?.start_time || interval?.end_time);
        }
        return true;
    };

    const seaSummary = useMemo(() => {
        if (seaWindows.length === 0) return copy.duty.noneRecorded;
        return seaWindows.map((window) => window.label).join(", ");
    }, [seaWindows, copy]);

    const ensureReason = (label: string, reason: string) => (label.includes(reason) ? label : `${label} (${reason})`);

    const computeStartOptions = ({
        index,
        intervals: currentIntervals,
    }: {
        index: number;
        intervals: IntervalFormValue[];
    }) => {
        return dutyStartOptions.map((option) => {
            const nextOption = { ...option };
            if (index > 0) {
                const previousEnd = currentIntervals[index - 1]?.end_time;
                if (previousEnd && option.value < previousEnd) {
                    nextOption.locked = true;
                    nextOption.label = ensureReason(option.label, copy.intervals.afterPrevious);
                }
            }
            return nextOption;
        });
    };

    const computeEndOptions = ({
        index,
        intervals: currentIntervals,
    }: {
        index: number;
        intervals: IntervalFormValue[];
    }) => {
        const startValue = currentIntervals[index]?.start_time;
        const base = getDutyEndOptions(seaWindows, startValue, copy.duty).map((option) => ({
            ...option,
        }));

        const nextStart = currentIntervals[index + 1]?.start_time;
        if (!nextStart) {
            return base;
        }

        return base.map((option) => {
            if (option.value > nextStart) {
                return {
                    ...option,
                    locked: true,
                    label: ensureReason(option.label, copy.intervals.beforeNext),
                };
            }
            return option;
        });
    };

    const handleMatchSeaWindows = useCallback(() => {
        if (!entryData || seaWindows.length === 0) return;

        const mapped = mapStoredIntervalsToForm(entryData.at_sea_intervals as IntervalFormValue[] | null | undefined, {
            fallbackFullDay: entryData.at_sea_all_day === true,
        }).filter((interval) => interval.start_time && interval.end_time);

        replace(mapped.length > 0 ? mapped : [{ start_time: "", end_time: "" }]);
    }, [entryData, seaWindows, replace]);

    const dutyIntervalError = useMemo(() => {
        const errors = formState.errors.on_duty_intervals;
        if (!errors) return null;

        if (Array.isArray(errors)) {
            const fieldError = errors.find((error) => error && "message" in error);
            if (fieldError && fieldError?.message) {
                return fieldError.message as string;
            }
        }

        if ("message" in errors && errors.message) {
            return errors.message as string;
        }

        return null;
    }, [formState.errors.on_duty_intervals]);

    if (isLoading) {
        return (
            <FormStepWrapper currentStep={3}>
                <FormLoading />
            </FormStepWrapper>
        );
    }

    if (!entryData || entryData.at_sea === false) {
        return (
            <FormStepWrapper currentStep={3}>
                <div className="text-slate-600 text-sm text-center">{copy.duty.notAtSea}</div>
            </FormStepWrapper>
        );
    }

    return (
        <FormStepWrapper currentStep={3}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 py-4 w-full max-w-2xl">
                    <h2 className="block font-semibold text-xl text-center">{copy.duty.question}</h2>

                    <div className="bg-slate-50 p-4 border border-slate-200 rounded-lg text-slate-700 text-sm">
                        <div className="flex justify-between items-center gap-4">
                            <div>
                                <p className="font-medium">{copy.duty.todayLabel}</p>
                                <p>{seaSummary}</p>
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleMatchSeaWindows}
                                disabled={seaWindows.length === 0}
                            >
                                {copy.duty.match}
                            </Button>
                        </div>
                    </div>

                    <IntervalFieldCards
                        namePrefix="on_duty_intervals"
                        fields={fields as any}
                        intervals={intervals as IntervalFormValue[]}
                        computeStartOptions={computeStartOptions}
                        computeEndOptions={computeEndOptions}
                        getErrors={getIntervalErrors}
                        canRemove={canRemoveInterval}
                        onRemove={handleRemoveInterval}
                    />

                    <div className="flex justify-center">
                        <Button type="button" variant="outline" onClick={handleAddInterval}>
                            {copy.duty.add}
                        </Button>
                    </div>

                    {dutyIntervalError && (
                        <div className="bg-red-50 mt-4 p-3 border border-red-200 rounded-lg">
                            <p className="text-red-800 text-sm">{dutyIntervalError}</p>
                        </div>
                    )}

                    <FormButtons submitting={submitting} showPrevious onPrevious={onPrevious} />
                </form>
            </Form>
        </FormStepWrapper>
    );
}
